import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import {
  Animated,
  Easing,
  Dimensions,
  StyleSheet,
  View,
  Platform,
  Linking,
} from 'react-native'

import { NAVIGATION_BACK, transitions } from '@adalo/constants'

import { BackHandler } from 'utils/history'
import {
  TARGET_FORGOT_PASSWORD,
  TARGET_RESET_PASSWORD,
} from 'utils/forgotPassword'

import { register } from 'utils/notification-wrapper'
import { createStack, push, peek, pop } from 'utils/stacks'
import { registerDevice, parseURL } from 'utils/notifications'
import { TRANSITION_CONFIG } from 'utils/transitions'
import { unsafeGetToken } from 'ducks/auth'
import { showNotification } from 'ducks/notifications'
import { resetRandomNumbers } from 'ducks/random'

import Screen from './Screen'
import ErrorScreen from './Error'
import { ForgotPassword, ResetPassword } from './ForgotPassword'

export const SLIDE_TRANSITIONS = [
  transitions.SLIDE_LEFT,
  transitions.SLIDE_RIGHT,
  transitions.SLIDE_UP,
  transitions.SLIDE_DOWN,
]

export const FLOAT_TRANSITIONS = [
  transitions.FLOAT_LEFT,
  transitions.FLOAT_RIGHT,
  transitions.FLOAT_UP,
  transitions.FLOAT_DOWN,
]

class Navigator extends Component {
  static childContextTypes = {
    navigate: PropTypes.func,
    getDeviceId: PropTypes.func,
  }

  static contextTypes = {
    getTransitionStyle: PropTypes.func,
    getNotificationsURL: PropTypes.func,
  }

  constructor(props) {
    super(props)

    this.setupRoutes(props)
  }

  getChildContext() {
    return {
      navigate: this.navigate,
      getDeviceId: this.getDeviceId,
    }
  }

  getDeviceId = () => {
    const { deviceId } = this.state
    return deviceId
  }

  handleRegister = async (deviceId, isPreviewer = false) => {
    let { app, authenticated } = this.props
    let { getNotificationsURL } = this.context

    if (deviceId && deviceId.token) {
      deviceId = deviceId.token
    }

    this.setState({ deviceId })

    if (authenticated) {
      await registerDevice(
        app,
        deviceId,
        getNotificationsURL(),
        unsafeGetToken(null, app),
        isPreviewer
      )
    }
  }

  handleNotification = (route, notif = null) => {
    if (notif) {
      return this.handleForegroundNotification(route, notif)
    } else {
      return this.handleNotificationSub(route)
    }
  }

  handleNotificationSub = route => {
    // handle
    let { initialComponentId } = this.props

    let routeStack = createStack({
      target: initialComponentId,
      params: {},
    })

    if (route.target !== routeStack[0].target) {
      routeStack = push(routeStack, this.getRoute(route), false)
    }

    this.setState({
      routeStack,
    })
  }

  handleForegroundNotification = (route, notif) => {
    let { showNotification } = this.props

    showNotification(notif, () => this.handleNotificationSub(route))
  }

  getRoute(route) {
    return {
      params: {},
      transition: transitions.PUSH,
      ...route,
    }
  }

  setupRoutes = (initialProps = null) => {
    let props = initialProps || this.props
    let { initialRoute, deviceId } = props

    let routeStack = createStack({
      target: props.initialComponentId,
      params: {},
      transition: null,
    })

    if (initialRoute) {
      routeStack = push(routeStack, this.getRoute(initialRoute))
    }

    let newState = {
      routeStack,
      deviceId,
      baseOffset: -1,
      navigationInProgress: false,
      currentViewOffset: new Animated.Value(0),
    }

    this.notify(peek(routeStack))

    if (initialProps !== null) {
      this.state = newState
    } else {
      this.setState(newState)
    }
  }

  isAndroid = () => {
    let { getTransitionStyle } = this.context

    let transitionStyle = getTransitionStyle()

    return (transitionStyle || Platform.OS) === 'android'
  }

  getTiming = transitionType => {
    let animationTiming = this.isAndroid() ? 200 : 500

    if (transitionType === transitions.NONE) {
      return 0
    }

    return animationTiming
  }

  goBack = (...args) => {
    const { routeStack } = this.state

    if (routeStack.length > 1) {
      this.navigateBack()
      return true
    }

    return false
  }

  goBackWrapped = () => {
    if (window.history.length > 1) {
      window.history.go(-1)
    } else {
      this.navigateBack()
    }
  }

  navigateBack = () => {
    const { currentViewOffset, routeStack, baseOffset } = this.state
    const { resetRandomNumbers } = this.props

    const currentRoute = peek(routeStack)
    if (!currentRoute) {
      return
    }

    if (routeStack.length <= 1) {
      return
    }

    resetRandomNumbers()

    Animated.timing(currentViewOffset, {
      toValue: baseOffset,
      easing: Easing.bezier(0.3, 1.06, 0.65, 1),
      duration: this.getTiming(currentRoute.transition),
      delay: 50,
      useNativeDriver: true,
    }).start(this.postPop)

    this.notify(peek(routeStack, 1))

    this.setState({ navigationInProgress: true })
  }

  handlePasswordReset = () => {
    const { userUsedTemporaryLogin } = this.props
    if (userUsedTemporaryLogin) {
      this.navigate({
        target: TARGET_RESET_PASSWORD,
        transition: transitions.MODAL,
      })
    }
  }

  navigate = action => {
    let {
      currentViewOffset,
      routeStack,
      baseOffset,
      navigationInProgress,
    } = this.state

    let currentRoute = peek(routeStack)
    if (!currentRoute) {
      return
    }
    if (navigationInProgress) {
      return
    }

    if (action.target === NAVIGATION_BACK || action.type === NAVIGATION_BACK) {
      if (Platform.OS === 'web') {
        this.goBackWrapped()
      } else {
        this.navigateBack()
      }
    } else {
      let timing = Animated.timing(currentViewOffset, {
        toValue: baseOffset + 2,
        easing: Easing.bezier(0.3, 1.06, 0.65, 1),
        duration: this.getTiming(action.transition),
        delay: 150,
        useNativeDriver: true,
      })

      window.setTimeout(() => {
        timing.start(() => {
          this.setState(
            {
              navigationInProgress: false,
            },
            () => this.handlePasswordReset()
          )
        })
      }, 0)

      let newParams = action.params
      let oldParams = currentRoute.params

      this.notify(action)

      let params = { ...oldParams, ...newParams }

      this.setState(state => ({
        baseOffset: baseOffset + 1,
        navigationInProgress: true,
        routeStack: push(state.routeStack, { ...action, params }),
      }))
    }
  }

  // Cleanup that happens after back animation finishes
  postPop = () => {
    let { baseOffset, routeStack: oldRouteStack } = this.state

    let [, routeStack] = pop(oldRouteStack)

    this.setState({
      routeStack,
      navigationInProgress: false,
      baseOffset: baseOffset - 1,
    })
  }

  setURL = url => {
    let { initialComponentId } = this.props

    // Prevent url.url from causing error
    if (!url) {
      return
    }

    let { screen, prevScreen, params } = parseURL(url.url)

    // This is not a formatted launch URL, so just go to home
    if (!screen) {
      return
    }

    // Catch case with no previous screen
    if (!prevScreen) {
      prevScreen = initialComponentId
    }

    let routeStack = createStack({
      target: prevScreen,
      params: {},
      transition: null,
    })

    routeStack = push(routeStack, {
      target: screen,
      params,
      transition: transitions.PUSH,
    })

    this.setState({
      routeStack,
      baseOffset: -1,
      navigationInProgress: false,
      currentViewOffset: new Animated.Value(0),
    })
  }

  notify = route => {
    let { onNavigate } = this.props

    if (!route || !onNavigate || !route.target) {
      return
    }

    onNavigate({ componentId: route.target })
  }

  componentDidMount() {
    let { skipNotifications, deviceId, resetRandomNumbers } = this.props
    const { routeStack } = this.state

    const currentTarget = peek(routeStack)?.target

    resetRandomNumbers(currentTarget)

    BackHandler.addEventListener('hardwareBackPress', this.goBack)

    Linking.getInitialURL().then(this.setURL)
    Linking.addEventListener('url', this.setURL)

    // Setup notifications if not defined
    if (Platform.OS !== 'web' && !skipNotifications) {
      register({
        onRegister: this.handleRegister,
        onNotification: this.handleNotification,
      })
    } else if (deviceId) {
      this.handleRegister(deviceId, true)
    }
  }

  componentWillUnmount() {
    BackHandler.removeEventListener('hardwareBackPress', this.goBack)

    Linking.removeEventListener('url', this.setURL)
  }

  componentDidUpdate(_, prevState) {
    const { resetRandomNumbers } = this.props

    const { routeStack, navigationInProgress } = this.state

    const currentTarget = peek(routeStack)?.target

    if (navigationInProgress) {
      resetRandomNumbers(currentTarget)
    }
  }

  render() {
    let { app, layoutGuides } = this.props
    let {
      routeStack,
      currentViewOffset,
      navigationInProgress,
      baseOffset,
    } = this.state

    let isAndroid = this.isAndroid()

    let { width, height } = Dimensions.get('window')

    let currentRoute = peek(routeStack)
    let previousRoute = peek(routeStack, 1)

    let currentRouteIndex = routeStack.length - 1
    let previousRouteIndex = routeStack.length - 2

    if (!currentRoute || !currentRoute.target) {
      window.setTimeout(() => {
        this.setupRoutes()
      }, 0)

      return null
    }

    let transition = currentRoute && currentRoute.transition

    if (!transition) {
      transition = transitions.PUSH
    }

    let inputRange = [baseOffset, baseOffset + 1]

    // Get basic styles

    let {
      transformType,
      transformDirection,
      underViewMultiple,
      dimUnderView,
      gap,
    } = TRANSITION_CONFIG[transition]

    gap = gap * transformDirection

    let transformDistance = transformType === 'translateX' ? width : height
    transformDistance = transformDistance * transformDirection

    // Cover cases for android push / modal

    let fadeStyles = {}
    if (
      isAndroid &&
      [transitions.MODAL, transitions.PUSH].indexOf(currentRoute.transition) !==
        -1
    ) {
      fadeStyles = {
        opacity: currentViewOffset.interpolate({
          inputRange,
          outputRange: [0, 1],
        }),
      }

      transformDistance = 100
      transformType = 'translateY'
    }

    let currentComponent = app.components[currentRoute.target]
    let previousComponent =
      previousRoute && app.components[previousRoute.target]

    let currentScreen
    // let previousScreen

    const datasourceIds = Object.keys(app.datasources)
    const datasourceId = datasourceIds[0]

    if (
      [TARGET_FORGOT_PASSWORD, TARGET_RESET_PASSWORD].includes(
        currentRoute.target
      )
    ) {
      if (currentRoute.target === TARGET_FORGOT_PASSWORD) {
        currentScreen = (
          <ForgotPassword
            layoutGuides={layoutGuides}
            datasourceId={datasourceId}
          />
        )
      } else {
        currentScreen = (
          <ResetPassword
            layoutGuides={layoutGuides}
            datasourceId={datasourceId}
          />
        )
      }
    } else {
      currentScreen = currentComponent ? (
        <Screen
          topScreen
          active={!navigationInProgress}
          component={currentComponent}
          params={currentRoute.params}
          layoutGuides={layoutGuides}
        />
      ) : (
        <ErrorScreen message="Screen Missing" />
      )
    }

    let previousScreen = previousComponent ? (
      <Screen
        active={false}
        topScreen={false}
        component={previousComponent}
        params={previousRoute.params}
        layoutGuides={layoutGuides}
      />
    ) : (
      <ErrorScreen message="Screen Missing" />
    )

    let children = []

    if (previousComponent) {
      children.push(
        <Animated.View
          style={[
            styles.inner,
            {
              transform: [
                {
                  [transformType]: currentViewOffset.interpolate({
                    inputRange,
                    outputRange: [
                      0,
                      -underViewMultiple * transformDistance - gap,
                    ],
                  }),
                },
              ],
            },
          ]}
          key={`${previousRouteIndex}-${previousRoute.target}`}
        >
          {previousScreen}
        </Animated.View>
      )

      if (dimUnderView) {
        children.push(
          <Animated.View
            key="fader"
            style={[
              styles.inner,
              styles.fader,
              {
                opacity: currentViewOffset.interpolate({
                  inputRange,
                  outputRange: [0, 1],
                }),
              },
            ]}
          />
        )
      }
    }

    children.push(
      <Animated.View
        key={
          currentRoute.target
            ? `${currentRouteIndex}-${currentRoute.target}`
            : '0'
        }
        style={[
          styles.inner,
          {
            ...fadeStyles,
            transform: [
              {
                [transformType]: currentViewOffset.interpolate({
                  inputRange,
                  outputRange: [transformDistance + gap, 0],
                }),
              },
            ],
          },
        ]}
      >
        {currentScreen}
      </Animated.View>
    )

    return <View style={styles.wrapper}>{children}</View>
  }
}

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    backgroundColor: '#222',
  },
  inner: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  fader: {
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
})

const mapStateToProps = state => ({
  userUsedTemporaryLogin: state.auth.userUsedTemporaryLogin,
})

export default connect(mapStateToProps, {
  showNotification,
  resetRandomNumbers,
})(Navigator)
